import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isBetween from 'dayjs/plugin/isBetween'
import type { Period } from 'types/periods'
import { v4 as uuidv4 } from 'uuid'

dayjs.extend(isBetween)
dayjs.extend(customParseFormat)
dayjs.extend(utc)
const dateFormatString = 'YYYY-MM-DD'

export enum PeriodLevels {
  MONTH = 'MONTH',
  QUARTERLY = 'QUARTERLY',
  BIANNUAL = 'BIANNUAL',
  ANNUAL = 'ANNUAL',
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

function formatDate(date: dayjs.Dayjs): string {
  return date.format('YYYY-MM-DD')
}

function eliminateLevelAtDepth(root: Period | null, targetDepth: number, currentDepth: number = 0): Period | null {
  // Base case: if root is null or has no children, return as is
  if (!root || root.children.length === 0) {
    return root
  }

  // If the next level is the target depth, eliminate that level
  if (currentDepth + 1 === targetDepth) {
    const newChildren: Period[] = root.children.flatMap((child) => child.children)
    return {
      ...root,
      children: newChildren,
    }
  }

  // Otherwise, recurse into children
  const updatedChildren: Period[] = root.children
    .map((child) => eliminateLevelAtDepth(child, targetDepth, currentDepth + 1))
    .filter((child): child is Period => child !== null)

  return {
    ...root,
    children: updatedChildren,
  }
}

export function generatePeriods(
  rootOrg: string,
  start: Date,
  level: PeriodLevels,
  years: number = 1,
  excludeLevels: PeriodLevels[] = []
): Period[] {
  // Convert the incoming Date to dayjs UTC and ensure it's the first of the month
  const rootPeriodStart = dayjs.utc(start).date(1).hour(0).minute(0).second(0).millisecond(0)
  const year = rootPeriodStart.year().toString()
  const rootPeriodEnd = rootPeriodStart.add(1, 'year').subtract(1, 'day')

  const newPeriods: Period[] = []

  for (let y = 0; y < years; y++) {
    const yStartDate = rootPeriodStart.add(y, 'year')
    const yEndDate = rootPeriodEnd.add(y, 'year')
    const yPeriod: Period = {
      id: uuidv4(),
      organizationId: rootOrg,
      label: `FY-${Number.parseInt(year) + y}`,
      startDate: formatDate(yStartDate),
      endDate: formatDate(yEndDate),
      isClosed: false,
      children: [],
      createdAt: dayjs.utc().toISOString(),
      updatedAt: dayjs.utc().toISOString(),
      deletedAt: null,
    }

    // Generate half-year periods
    for (let h = 0; h < 2; h++) {
      const hStartDate = yStartDate.add(6 * h, 'month')
      const hEndDate = hStartDate.add(6, 'month').subtract(1, 'day')

      const hPeriod: Period = {
        id: uuidv4(),
        organizationId: rootOrg,
        label: `FY-${Number.parseInt(year) + y} H${h + 1}`,
        startDate: formatDate(hStartDate),
        endDate: formatDate(hEndDate),
        isClosed: false,
        children: [],
        createdAt: dayjs.utc().toISOString(),
        updatedAt: dayjs.utc().toISOString(),
        deletedAt: null,
      }

      yPeriod.children.push(hPeriod)

      // Generate quarters
      for (let q = 0; q < 2; q++) {
        const qStartDate = hStartDate.add(3 * q, 'month')
        const qEndDate = qStartDate.add(3, 'month').subtract(1, 'day')

        const qPeriod: Period = {
          id: uuidv4(),
          organizationId: rootOrg,
          label: `FY-${Number.parseInt(year) + y} Q${h * 2 + q + 1}`,
          startDate: formatDate(qStartDate),
          endDate: formatDate(qEndDate),
          isClosed: false,
          children: [],
          createdAt: dayjs.utc().toISOString(),
          updatedAt: dayjs.utc().toISOString(),
          deletedAt: null,
        }

        hPeriod.children.push(qPeriod)

        // Generate months
        for (let m = 0; m < 3; m++) {
          const mStartDate = qStartDate.add(m, 'month')
          const mEndDate = mStartDate.add(1, 'month').subtract(1, 'day')

          const mPeriod: Period = {
            id: uuidv4(),
            organizationId: rootOrg,
            label: `FY-${Number.parseInt(year) + y} ${MONTHS[mStartDate.month()]}`,
            startDate: formatDate(mStartDate),
            endDate: formatDate(mEndDate),
            isClosed: false,
            children: [],
            createdAt: dayjs.utc().toISOString(),
            updatedAt: dayjs.utc().toISOString(),
            deletedAt: null,
          }

          qPeriod.children.push(mPeriod)
        }
      }
    }

    // eliminate the levels that are not needed
    let temp: Period | null = JSON.parse(JSON.stringify(yPeriod))
    if (excludeLevels.includes(PeriodLevels.MONTH)) {
      temp = eliminateLevelAtDepth(temp, 3)
    } else if (excludeLevels.includes(PeriodLevels.QUARTERLY)) {
      temp = eliminateLevelAtDepth(temp, 2)
    } else if (excludeLevels.includes(PeriodLevels.BIANNUAL)) {
      temp = eliminateLevelAtDepth(temp, 1)
    }
    if (temp) {
      newPeriods.push(temp)
    }
  }
  return newPeriods
}

// Check if the date in already covered by the existing periods. This is done at the root level
// of the tree of periods. If the newDate isBetween the dates of a root period then the period
// range cover the date of the newDate.
export function checkDateAgainstExistingPeriods(existingPeriods: Period[], newDate: dayjs.Dayjs): boolean {
  if (!existingPeriods) {
    return false
  }
  return existingPeriods.some((p) =>
    newDate.isBetween(dayjs.utc(p.startDate, dateFormatString), dayjs.utc(p.endDate, dateFormatString), null, '[]')
  )
}

// Check if any of the newly generate periods conflict with the existing periods.
export function hasPeriodOverlapAgainstExistingPeriods(existingPeriods: Period[], newPeriods: Period[]): boolean {
  // Loop through the root periods of newPeriods
  if (!existingPeriods) {
    return false
  }
  for (const newPeriod of newPeriods) {
    // Check if this new period overlaps with any existing period
    const hasOverlap = existingPeriods.some(
      (existingPeriod) =>
        dayjs
          .utc(newPeriod.startDate, dateFormatString)
          .isBetween(
            dayjs.utc(existingPeriod.startDate, dateFormatString),
            dayjs.utc(existingPeriod.endDate, dateFormatString),
            null,
            '[]'
          ) ||
        dayjs
          .utc(newPeriod.endDate, dateFormatString)
          .isBetween(
            dayjs.utc(existingPeriod.startDate, dateFormatString),
            dayjs.utc(existingPeriod.endDate, dateFormatString),
            null,
            '[]'
          ) ||
        dayjs
          .utc(existingPeriod.startDate, dateFormatString)
          .isBetween(
            dayjs.utc(newPeriod.startDate, dateFormatString),
            dayjs.utc(newPeriod.endDate, dateFormatString),
            null,
            '[]'
          )
    )

    return hasOverlap
  }

  return false
}

export function getCurrentRootPeriod(periods: Period[]): Period | null {
  if (!periods || periods.length === 0) {
    return null
  }
  const currentDate = dayjs.utc()
  const currentPeriod = periods.find((p) =>
    currentDate.isBetween(dayjs.utc(p.startDate, dateFormatString), dayjs.utc(p.endDate, dateFormatString), null, '[]')
  )

  return currentPeriod || null
}
